"use client";

import { FiSearch } from "react-icons/fi";
import MainHeader from "../components/MainHeader";
import BottomNavigationBar from "../components/BottomNavigationBar";
import BlogHeader from "../components/blog/mobile/BlogHeader";
import Image from "next/image";
import { useNotFound } from "@/hooks/customer/useNotFound";
import Footer from "../components/desktop/FooterDesktop";

const NotFound = () => {
  const { router, query, setQuery, redirectSeconds, handleSearch } =
    useNotFound();

  return (
    <div className="flex flex-col min-h-screen">
      {/* ── Headers ── */}
      <div className="lg:hidden">
        <BlogHeader />
      </div>
      <div className="hidden lg:block">
        <MainHeader />
        <BottomNavigationBar />
      </div>

      {/* ── 404 Section — fills all remaining viewport height ── */}
      <main className="relative flex flex-col items-center justify-center overflow-hidden bg-[#FFDF82] flex-1 min-h-[calc(100vh-140px)]">
        {/* ────── Background Bubbles ────── */}

        {/* TOP-LEFT bubble 1 – large */}
        <div className="absolute -top-27.5 -left-27.5 rounded-full pointer-events-none w-80 h-80 bg-[#F5C518] opacity-30" />
        {/* TOP-LEFT bubble 2 – small */}
        <div className="absolute rounded-full pointer-events-none w-80 h-80 bg-[#F5C518] opacity-[0.25] top-5 left-20" />

        {/* BOTTOM-RIGHT bubble 1 – large */}
        <div className="absolute -top-25 -left-22.5 rounded-full pointer-events-none w-80 h-80 bg-[#F5C518] opacity-[0.35]" />
        {/* BOTTOM-RIGHT bubble 2 – small */}
        <div className="absolute -top-12.5 -left-12.5 rounded-full pointer-events-none w-80 h-80 bg-[#F5C518] opacity-[0.25]" />

        {/* ────── Decorative illustrations ────── */}

        {/* Aeroplane – top right */}
        <Image
          src="/images/aeroplane.png"
          alt="Aeroplane"
          width={259}
          height={230}
          className="absolute pointer-events-none top-[24px] right-[40px] w-[clamp(120px,14vw,190px)] opacity-[0.9]"
        />

        {/* Palm tree – bottom left */}
        <Image
          src="/images/tree.png"
          alt="Palm tree"
          width={247}
          height={282}
          className="absolute pointer-events-none bottom-0 left-[24px] w-[clamp(110px,12vw,170px)] opacity-[0.9]"
        />

        {/* ────── Main Content ────── */}
        <div className="relative z-10 flex flex-col items-center gap-5 px-4 w-full max-w-2xl py-10">
          {/* Search bar */}
          <form
            onSubmit={handleSearch}
            className="w-full flex items-center bg-white rounded-full shadow-sm max-w-130 px-5 py-2.5"
          >
            <FiSearch className="text-gray-400 mr-3 shrink-0 h-4.5 w-4.5" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find your next place or adventure."
              className="flex-1 text-sm text-gray-400 bg-transparent focus:outline-none"
            />
          </form>

          {/* 404 + compass row */}
          <div className="flex items-center justify-center select-none gap-2">
            <span className="font-extrabold text-white leading-none text-[clamp(110px,19vw,200px)] drop-shadow-[0_2px_16px_rgba(0,0,0,0.08)]">
              4
            </span>

            <div className="relative inline-flex shrink-0 items-center justify-center">
              <div
                aria-hidden
                className="absolute rounded-full pointer-events-none left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#F5C518] opacity-[0.3] w-[calc(clamp(110px,13vw,170px)*1.1)] h-[calc(clamp(110px,13vw,170px)*1.1)]"
              />
              <Image
                src="/images/compass.png"
                alt="Compass"
                width={319}
                height={306}
                className="relative z-10 shrink-0 object-contain w-[clamp(110px,13vw,170px)] h-[clamp(110px,13vw,170px)]"
              />
            </div>

            <span className="font-extrabold text-white leading-none text-[clamp(110px,19vw,200px)] drop-shadow-[0_2px_16px_rgba(0,0,0,0.08)]">
              4
            </span>
          </div>

          {/* Message */}
          <p className="text-center font-bold text-black text-[clamp(16px,2vw,22px)]">
            Oops! This destination is off the map.
          </p>

          {/* CTA Button */}
          <button
            type="button"
            onClick={() => router.push("/")}
            className="rounded-full font-semibold text-black transition-all hover:brightness-95 active:scale-95 bg-[#FBBC05] px-12 py-3 text-[clamp(13px,1.4vw,16px)] shadow-[0_2px_8px_rgba(0,0,0,0.10)] cursor-pointer"
          >
            {redirectSeconds > 0
              ? `Returning to the home page in ${redirectSeconds}s…`
              : "Redirecting…"}
          </button>
        </div>
      </main>

      {/* FooterDesktop uses .ta-desktop-home-footer (2rem margin-top on lg); pull flush to 404 block */}
      <div className="shrink-0 lg:[&_footer]:!mt-0">
        <Footer />
      </div>
    </div>
  );
};

export default NotFound;
